import {makeStyles} from "tss-react/mui";
import {sidebarWidth, sidebarClass} from "../../resources/environment";

export const useSidebarStyles = makeStyles()((theme, {isSidebarExpanded} = {}) => ({
  root: {
    display: "flex",
  },
  sidebarContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  drawer: {
    zIndex: theme.zIndex.drawer + 1,
    width: isSidebarExpanded ? sidebarWidth.open : sidebarWidth.collapsed,
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [`&.${sidebarClass}`]: {
      width: sidebarWidth.collapsed,
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    "@media print": {
      display: "none",
    },
  },
  drawerPaper: {
    width: "inherit",
    maxHeight: "100vh",
    height: "100vh",
    paddingTop: theme.spacing(1),
    overflowX: "hidden",
    borderRight: "none",
    boxShadow: `${theme.palette.layout.sidebar.boxShadow} !important`,
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.layout.scrollbar.thumb} ${theme.palette.layout.scrollbar.track}`,
    "&::-webkit-scrollbar": {
      width: "0.4rem",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.layout.scrollbar.thumb,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.layout.scrollbar.track,
    },
  },
  drawerContainer: {
    overflow: "auto",
  },
  overlay: {
    minWidth: 200,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  parentTitle: {
    paddingLeft: theme.spacing(2),
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  mobileMenuButton: {
    position: "fixed",
    top: 12,
    left: 12,
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const useSidebarContentStyles = makeStyles()((theme, {isSidebarExpanded} = {}) => ({
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  container: {
    backgroundColor: theme.palette?.draggable?.accent,
    cursor: "default",
  },
  headerBox: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: "flex",
    alignItems: "flex-end",
    justifyContent: isSidebarExpanded ? "space-between" : "center",
  },
  headerIconButton: {
    color: theme.palette.layout.icon.color,
    "& .sidebar-logo-menu": {
      display: "none",
    },
    // Swap the shield for the menu icon only where hovering exists; on touch the
    // equivalent JS hover handlers made the first tap register as a hover, not a click
    "@media (hover: hover)": {
      "&:hover .sidebar-logo-shield": {
        display: "none",
      },
      "&:hover .sidebar-logo-menu": {
        display: "block",
      },
    },
  },
}));

export const useSidebarItemStyles = makeStyles()(
  (theme, {isTextHidden, isCurrentPage, isCurrentParent, inOverlay}) => ({
    menuList: {
      "& a .MuiListItemIcon-root": {
        marginLeft: 0,
        minWidth: theme.spacing(5),
      },
      "& a": {
        justifyContent: "flex-start",
        paddingLeft: theme.spacing(2),
      },
    },
    item: {
      justifyContent: inOverlay ? "flex-start" : "center",
      minHeight: 44,
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
      paddingLeft: inOverlay ? theme.spacing(2) : undefined,
      position: "relative",
      boxShadow:
        isCurrentPage || isCurrentParent ?
          `inset -3px 0 0 ${theme.palette.primary.main}` :
          undefined,
      background:
        (isCurrentPage || isCurrentParent) && theme.palette.mode !== "dark" ?
          theme.palette.primary.megaLight :
          undefined,
      "&:hover": {
        background: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main} !important`,
        "& div p, & div div": {color: theme.palette.background.default},
        "& div svg, svg": {fill: theme.palette.background.default},
      },
    },
    itemText: {
      display: "none",
      flexDirection: "column",
      alignItems: "flex-start",
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
    },
    overlayItemText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
    },
    labelChip: {
      height: "60%",
      fontSize: "0.55rem",
      fontWeight: 600,
      marginLeft: 5,
      marginRight: -5,
      mt: 0.5,
      "& .MuiChip-label": {
        px: 0.4,
      },
    },
    chevronRight: {
      position: "absolute",
      right: 4,
      fontSize: !isTextHidden ? "1.25rem" : "0.65rem",
    },
  }),
);

export const useSidebarCreateButtonStyles = makeStyles()(() => ({
  primaryButton: {
    textTransform: "none",
    flex: 1,
  },
  dropdownButton: {
    maxWidth: 32,
  },
}));

export const useSidebarIconStyles = makeStyles()((theme) => ({
  listItemIcon: {
    color: theme?.palette?.layout?.icon?.color,
    justifyContent: "center",
    minWidth: theme.spacing(5),
  },
}));
